import { Alert, ExclamationTriangleIcon, Flex, Text } from "@fluentui/react-northstar";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { StripeCoreCheckoutPage } from "../../../external/payment/StripeCoreCheckoutPage";
import { useDatadog } from "../../actions/useDatadog";
import { useUserInfo } from "../../actions/useUserInfo";
import CautionIcon from "../../assets/caution-icon.svg";
import { isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { isMobileClient } from "../../utils/clientHelper";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { CheckoutPlan, getMSTeamsPlanLevelFrequencies } from "../../utils/paymentHelpers";
import { getPageContext } from "../../utils/teamsHelpers";
import { TeamsLoader } from "../TeamsLoader";
import { Congratulations } from "./Congratulations";

export const NativeCheckoutPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [plan, setPlan] = useState<CheckoutPlan>();
  const [showSuccess, setShowSuccess] = useState<{
    trialEnd?: Date;
  }>();
  const [error, setError] = useState<boolean>(false);

  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const { search } = useLocation();

  const {
    initialize: dataDogInit,
    startSessionRecording,
    stopSessionRecording,
  } = useDatadog("teams-core-checkout-native", 100, 100);

  const { app, isMobile } = useMemo(() => {
    return { app: getPageContext(), isMobile: isMobileClient(teamsContext) };
  }, [teamsContext]);

  const { isTrial, trackingSource } = useMemo(() => {
    const params = new URLSearchParams(search);

    const _isTrial = params.get("trial");
    const trackingSource = params.get("trackingSource");
    return { isTrial: _isTrial === "true", trackingSource };
  }, [search]);

  useEffect(() => {
    if (!user) {
      return;
    }
    dataDogInit(user.userId);
    startSessionRecording();
    return () => {
      stopSessionRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (isMobile) {
      return;
    }
    clientEventTracker.track("checkout viewed", user, {
      app,
      trackingSource,
      isTrial,
      native: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingSource]);

  useEffect(() => {
    if (isMobile) {
      return;
    }
    (async () => {
      setIsLoading(true);
      const planLevels = await paymentsApi.getPricing(teamsContext);
      setIsLoading(false);
      if (!isError(planLevels)) {
        setError(false);
        setPlan({
          planLevels: planLevels,
          planType: "licensed",
          quantity: 1,
          frequency: "yearly",
          frequencyOptions: getMSTeamsPlanLevelFrequencies(planLevels),
        });
      } else {
        setError(true);
      }
    })();
  }, [isMobile, teamsContext]);

  if (error) {
    return (
      <Flex column fill vAlign="center" hAlign="center">
        <Alert
          danger
          icon={<ExclamationTriangleIcon />}
          content="Oops, something went wrong while fetching pricing plans. Please try again or contact support"
          warning
        />
      </Flex>
    );
  }

  if (showSuccess) {
    return (
      <Flex column fill vAlign="center" hAlign="center">
        <Congratulations trialEnd={showSuccess.trialEnd} plan={"msteams-standard"} />
      </Flex>
    );
  }

  if (isMobile) {
    return (
      <Flex fill column vAlign="center" hAlign="center" gap="gap.medium">
        <img src={CautionIcon} height={45} alt="Polly" />
        <Text
          size="large"
          weight="bold"
          align="center"
          content="Oops! We can't access plans on mobile devices"
        />
        <Text size="small" weight="light" align="center">
          Please try again using a desktop computer or{" "}
          <a href="https://www.polly.ai/contact" target="__blank" style={{ color: "#0097FE" }}>
            contact us for help
          </a>
        </Text>
      </Flex>
    );
  }

  return (
    <TeamsLoader isLoading={!!isLoading} loadingMessage="Loading ...">
      {!isLoading && !error && plan && (
        <StripeCoreCheckoutPage
          pricingPlans={plan.planLevels!}
          user={user}
          isTrial={isTrial}
          onCheckoutComplete={(success, trialEnd) => {
            if (success) {
              setShowSuccess({ trialEnd });
            }
          }}
        />
      )}
    </TeamsLoader>
  );
};
